import { useState, useEffect } from "react";
import apiClient from "@/lib/api";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Users,
  Building,
  Search,
  Shield,
  UserCog,
  RefreshCw,
  Check,
  Database,
  Clock,
  AlertCircle,
  UserPlus,
  Trash2,
  Mail,
  Key,
  Activity,
  Server,
  CheckCircle2,
  XCircle
} from "lucide-react";

const Settings = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [cacheStatus, setCacheStatus] = useState(null);
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  
  // New user modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    name: "",
    password: "",
    role: "user",
    company_ids: []
  });
  const [creating, setCreating] = useState(false);
  
  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, companiesRes, cacheRes] = await Promise.all([
        apiClient.get(`/api/admin/users`),
        apiClient.get(`/api/admin/all-companies`),
        apiClient.get(`/api/cache/status`)
      ]);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
      setCacheStatus(cacheRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefreshCache = async () => {
    setRefreshingCache(true);
    try {
      const response = await apiClient.post(`/api/cache/refresh`);
      if (response.data.success) {
        toast.success("Data cache refreshed successfully");
        setCacheStatus(response.data.stats);
        // Also refresh diagnostics after cache refresh
        runDiagnostics();
      } else {
        toast.error(response.data.message || "Failed to refresh cache");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh cache");
    } finally {
      setRefreshingCache(false);
    }
  };

  const runDiagnostics = async () => {
    setLoadingDiagnostics(true);
    try {
      const response = await apiClient.get(`/api/diagnostics`);
      setDiagnostics(response.data);
    } catch (error) {
      toast.error("Failed to run diagnostics");
      setDiagnostics({
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await apiClient.post(`/api/admin/users/${userId}/role?role=${newRole}`);
      toast.success("Role updated successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const openAssignModal = (targetUser) => {
    setSelectedUser(targetUser);
    setSelectedCompanies(targetUser.companies?.map(c => c.company_id) || []);
    setAssignModalOpen(true);
  };

  const handleCompanyToggle = (companyId) => {
    setSelectedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const saveCompanyAssignments = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      await apiClient.post(
        `/api/admin/users/assign-companies`,
        { user_id: selectedUser.user_id, company_ids: selectedCompanies }
      );
      toast.success("Company access updated");
      setAssignModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update company access");
    } finally {
      setSaving(false);
    }
  };

  // Create user handlers
  const openCreateModal = () => {
    setNewUser({
      email: "",
      name: "",
      password: "",
      role: "user",
      company_ids: []
    });
    setCreateModalOpen(true);
  };

  const handleNewUserCompanyToggle = (companyId) => {
    setNewUser(prev => ({
      ...prev,
      company_ids: prev.company_ids.includes(companyId)
        ? prev.company_ids.filter(id => id !== companyId)
        : [...prev.company_ids, companyId]
    }));
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (newUser.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setCreating(true);
    try {
      await apiClient.post(`/api/admin/users/create`, newUser);
      toast.success("User created successfully");
      setCreateModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  // Delete user handlers
  const openDeleteModal = (targetUser) => {
    setUserToDelete(targetUser);
    setDeleteModalOpen(true);
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await apiClient.delete(`/api/admin/users/${userToDelete.user_id}`);
      toast.success("User deleted");
      setDeleteModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      u.name?.toLowerCase().includes(search) ||
      u.email?.toLowerCase().includes(search)
    );
  });

  const formatDateTime = (isoString) => {
    if (!isoString) return "Never";
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight flex items-center gap-2">
            <UserCog className="w-6 h-6 text-primary" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage users, company access, and data cache
          </p>
        </div>
      </div>

      {/* Data Cache Section */}
      <Card data-testid="cache-section">
        <CardHeader>
          <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            Data Cache
          </CardTitle>
          <CardDescription>
            Pay rules and wage laws are cached in memory for faster performance. Refresh to load latest data from SQL Server.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Last Refresh
                </p>
                <p className="text-sm font-medium">
                  {formatDateTime(cacheStatus?.last_refresh)}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Pay Rules</p>
                <p className="text-sm font-medium">{cacheStatus?.pay_rules_count || 0}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Wage Laws</p>
                <p className="text-sm font-medium">{cacheStatus?.wage_laws_count || 0}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Companies</p>
                <p className="text-sm font-medium">{cacheStatus?.companies_count || 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {cacheStatus?.is_stale && (
                <Badge variant="outline" className="text-amber-500 border-amber-500/30">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Stale
                </Badge>
              )}
              <Button
                onClick={handleRefreshCache}
                disabled={refreshingCache}
                data-testid="refresh-cache-btn"
              >
                {refreshingCache ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh Data
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Diagnostics Section */}
      <Card data-testid="diagnostics-section">
        <CardHeader>
          <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            System Diagnostics
          </CardTitle>
          <CardDescription>
            Test ODBC driver and SQL Server connectivity. Use this to troubleshoot deployment issues.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button
              onClick={runDiagnostics}
              disabled={loadingDiagnostics}
              variant="outline"
              data-testid="run-diagnostics-btn"
            >
              {loadingDiagnostics ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Running Tests...
                </>
              ) : (
                <>
                  <Activity className="w-4 h-4 mr-2" />
                  Run Diagnostics
                </>
              )}
            </Button>

            {diagnostics && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {/* ODBC Driver Status */}
                <Card className={`border ${diagnostics.odbc_driver?.installed ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {diagnostics.odbc_driver?.installed ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <span className="font-medium">ODBC Driver</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {diagnostics.odbc_driver?.installed ? (
                        <span className="text-green-600">{diagnostics.odbc_driver.version}</span>
                      ) : (
                        <span className="text-red-600">{diagnostics.odbc_driver?.error || 'Not installed'}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* SQL Server Status */}
                <Card className={`border ${diagnostics.sql_server?.connected ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {diagnostics.sql_server?.connected ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <span className="font-medium">SQL Server</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {diagnostics.sql_server?.connected ? (
                        <span className="text-green-600">Connected to {diagnostics.sql_server.server}</span>
                      ) : (
                        <span className="text-red-600">{diagnostics.sql_server?.error || 'Not connected'}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Cache Status */}
                <Card className={`border ${diagnostics.cache?.has_data ? 'border-green-500/30 bg-green-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {diagnostics.cache?.has_data ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-amber-500" />
                      )}
                      <span className="font-medium">Data Cache</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {diagnostics.cache?.has_data ? (
                        <span className="text-green-600">{diagnostics.cache.pay_rules_count} rules cached</span>
                      ) : (
                        <span className="text-amber-600">No data cached - click Refresh Data</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {diagnostics?.error && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-600">
                <p className="font-medium">Diagnostics Error:</p>
                <p className="text-sm">{diagnostics.error}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold font-['Manrope']">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Admins</p>
                <p className="text-2xl font-bold font-['Manrope']">
                  {users.filter(u => u.role === "admin").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                <Building className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Companies</p>
                <p className="text-2xl font-bold font-['Manrope']">{companies.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card data-testid="users-table">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-lg font-['Manrope']">User Management</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-users-input"
                />
              </div>
              <Button variant="outline" onClick={fetchData} data-testid="refresh-users-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button onClick={openCreateModal} data-testid="add-user-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[400px]">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Companies</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img
                            src={u.picture}
                            alt={u.name}
                            className="w-8 h-8 rounded-full"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                            <span className="text-sm font-medium">
                              {u.name?.charAt(0)?.toUpperCase() || "U"}
                            </span>
                          </div>
                        )}
                        <span className="font-medium">{u.name}</span>
                      </div>
                    </td>
                    <td className="text-muted-foreground">{u.email}</td>
                    <td>
                      <Select
                        value={u.role}
                        onValueChange={(value) => handleRoleChange(u.user_id, value)}
                        disabled={u.user_id === user?.user_id}
                      >
                        <SelectTrigger className="w-[100px]" data-testid={`role-select-${u.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {u.companies && u.companies.length > 0 ? (
                          <>
                            {u.companies.slice(0, 2).map((c) => (
                              <Badge key={c.company_id} variant="secondary" className="text-xs">
                                {c.company_name}
                              </Badge>
                            ))}
                            {u.companies.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{u.companies.length - 2} more
                              </Badge>
                            )}
                          </>
                        ) : u.role === "admin" ? (
                          <Badge variant="outline" className="text-xs">All Companies</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">No access</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openAssignModal(u)}
                          disabled={u.role === "admin"}
                          data-testid={`assign-companies-btn-${u.user_id}`}
                        >
                          <Building className="w-4 h-4 mr-1" />
                          Assign
                        </Button>
                        {u.user_id !== user?.user_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteModal(u)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                            data-testid={`delete-user-btn-${u.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Assign Companies Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="max-w-md" data-testid="assign-companies-modal">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Assign Companies</DialogTitle>
            <DialogDescription>
              Select which companies {selectedUser?.name} can access
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-3">
              {companies.map((company) => (
                <div
                  key={company.company_id}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-secondary/50"
                >
                  <Checkbox
                    id={company.company_id}
                    checked={selectedCompanies.includes(company.company_id)}
                    onCheckedChange={() => handleCompanyToggle(company.company_id)}
                    data-testid={`company-checkbox-${company.company_id}`}
                  />
                  <Label
                    htmlFor={company.company_id}
                    className="flex-1 cursor-pointer"
                  >
                    {company.company_name}
                  </Label>
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setAssignModalOpen(false)}
              data-testid="cancel-assign-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={saveCompanyAssignments}
              disabled={saving}
              data-testid="save-assign-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-md" data-testid="create-user-modal">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add New User
            </DialogTitle>
            <DialogDescription>
              Create a new user account and assign company access
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="new-user-name">Name *</Label>
              <Input
                id="new-user-name"
                placeholder="John Doe"
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                data-testid="new-user-name-input"
              />
            </div>
            
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="new-user-email">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="new-user-email"
                  type="email"
                  placeholder="john@company.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  className="pl-9"
                  data-testid="new-user-email-input"
                />
              </div>
            </div>
            
            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="new-user-password">Password *</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="new-user-password"
                  type="password"
                  placeholder="Min 6 characters"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  className="pl-9"
                  data-testid="new-user-password-input"
                />
              </div>
            </div>
            
            {/* Role */}
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger data-testid="new-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Company Access */}
            {newUser.role !== "admin" && (
              <div className="space-y-2">
                <Label>Company Access</Label>
                <ScrollArea className="h-[150px] border rounded-lg p-2">
                  <div className="space-y-2">
                    {companies.map((company) => (
                      <div
                        key={company.company_id}
                        className="flex items-center space-x-3 p-2 rounded hover:bg-secondary/50"
                      >
                        <Checkbox
                          id={`new-${company.company_id}`}
                          checked={newUser.company_ids.includes(company.company_id)}
                          onCheckedChange={() => handleNewUserCompanyToggle(company.company_id)}
                          data-testid={`new-company-checkbox-${company.company_id}`}
                        />
                        <Label
                          htmlFor={`new-${company.company_id}`}
                          className="flex-1 cursor-pointer text-sm"
                        >
                          {company.company_name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                {newUser.company_ids.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {newUser.company_ids.length} company(ies) selected
                  </p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCreateModalOpen(false)}
              data-testid="cancel-create-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={createUser}
              disabled={creating || !newUser.email || !newUser.name || !newUser.password}
              data-testid="save-create-btn"
            >
              {creating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="max-w-sm" data-testid="delete-user-modal">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteUser}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
